import React, { useState, useEffect, useCallback } from "react";
import { URLS, makeApi } from "Utils/apiURL";
import {
  unwrap,
  isTerminal,
  statusMeta,
  formatDate
} from "Pages/Dashboard/data-toolkit/dtHelpers";

// Lists a team's previous runs for one tool. Polls while any run is still in
// progress so the list updates live. `foundLabel` names the count column per tool
// (e.g. "Found" for find-email, "Valid" for validate).
const JobsList = ({
  tool = "FIND_EMAIL",
  foundLabel = "Found",
  refreshKey,
  onView,
  onDownload
}) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const res = await makeApi({
      url: `${URLS.dataToolkitJobs}?page=0&limit=50`,
      method: "GET"
    });
    const { status, data } = unwrap(res);
    if (status === 200 && Array.isArray(data)) {
      setJobs(data.filter(j => j.tool === tool));
    }
    setLoading(false);
  }, [tool]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (!jobs.some(j => !isTerminal(j.status))) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [jobs, load]);

  return (
    <div className="dtJobs">
      <div className="dtJobsHead">
        <div className="dtSectionTitle">Previous runs</div>
        <button className="dtIconBtn" onClick={load} title="Refresh">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
          </svg>
        </button>
      </div>

      {loading ? (
        <div className="dtMuted">Loading…</div>
      ) : jobs.length === 0 ? (
        <div className="dtEmpty">
          No runs yet. Your finished runs will show up here.
        </div>
      ) : (
        <div className="dtTableWrap">
          <table className="dtTable">
            <thead>
              <tr>
                <th>Name</th>
                <th>Created</th>
                <th>Status</th>
                <th>{foundLabel}</th>
                <th>Credits</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => {
                const m = statusMeta(job.status);
                const counts = job.counts || {};
                const total = counts.total || job.inputRowCount || 0;
                return (
                  <tr key={job.id}>
                    <td className="dtJobName">{job.name || "Untitled run"}</td>
                    <td>{formatDate(job.createTs)}</td>
                    <td>
                      <span className={`dtStatusPill ${m.cls}`}>{m.label}</span>
                    </td>
                    <td>
                      {counts.found || 0} / {total}
                    </td>
                    <td>{job.creditsCharged || 0}</td>
                    <td className="dtRowActions">
                      <button className="dtLinkBtn" onClick={() => onView(job)}>
                        View
                      </button>
                      {job.status === "PROCESSED" && (
                        <button
                          className="dtLinkBtn"
                          onClick={() => onDownload(job)}
                        >
                          Download
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default JobsList;
